<div class="bg-night text-white max-h-[90vh] overflow-hidden flex flex-col min-w-[340px] sm:min-w-[520px]">
  <div class="p-4 lg:p-6 border-b border-white/10 flex items-center justify-between">
    <h2 class="text-xl lg:text-2xl font-bold flex items-center gap-3">
      <span class="material-symbols-outlined text-neon">science</span>
      {{ 'pos.freestyle_drink.title' | translate }}
    </h2>
    <button
      type="button"
      (click)="close()"
      class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>

  <div class="flex-1 overflow-y-auto p-4 lg:p-6 space-y-5">
    <div>
      <p class="text-xs text-white/50 mb-2">{{ 'pos.freestyle_drink.search_label' | translate }}</p>
      <input
        type="text"
        [ngModel]="searchQuery()"
        (ngModelChange)="searchQuery.set($event)"
        [placeholder]="'pos.freestyle_drink.search_placeholder' | translate"
        class="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-neon"
        data-testid="search-input" />
    </div>

    <div>
      <p class="text-xs text-white/50 mb-2">{{ 'pos.freestyle_drink.available_products' | translate }}</p>
      @if (filteredProducts().length > 0) {
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
          @for (product of filteredProducts(); track product.id) {
            <button
              type="button"
              (click)="addIngredient(product)"
              class="bg-white/5 hover:bg-white/15 border border-white/10 hover:border-neon/50 rounded-xl px-3 py-2 text-left transition-all"
              data-testid="product-item">
              <p class="font-medium text-white text-sm truncate">{{ product.name }}</p>
              <p class="text-xs text-neon">{{ product.priceCents / 100 | number: '1.2-2' }} €</p>
            </button>
          }
        </div>
      } @else {
        <div class="text-center py-4 text-white/40">
          <p class="text-sm">{{ 'pos.freestyle_drink.no_products' | translate }}</p>
        </div>
      }
    </div>

    <div>
      <p class="text-xs text-white/50 mb-2">{{ 'pos.freestyle_drink.selected_ingredients' | translate }}</p>
      @if (selectedIngredients().length > 0) {
        <div class="space-y-2">
          @for (item of selectedIngredients(); track item.product.id; let i = $index) {
            <div class="flex items-center justify-between gap-3 bg-white/5 rounded-xl px-4 py-3">
              <div class="flex-1 min-w-0">
                <p class="font-medium text-white truncate">{{ item.product.name }}</p>
                <p class="text-xs text-white/40">
                  {{ item.product.priceCents / 100 | number: '1.2-2' }} € /
                  @if (item.product.volumeMl != null) {
                    {{ 'common.unit_ml' | translate }}
                  } @else {
                    {{ 'common.unit_pcs' | translate }}
                  }
                </p>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  (click)="adjustQty(i, -1)"
                  [disabled]="isAtMinQty(item)"
                  class="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  data-testid="qty-decrease">
                  <span class="material-symbols-outlined text-base">remove</span>
                </button>

                <span
                  class="w-8 text-center font-bold text-white"
                  data-testid="qty-display">
                  {{ item.qty }}
                </span>

                <button
                  type="button"
                  (click)="adjustQty(i, 1)"
                  class="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
                  data-testid="qty-increase">
                  <span class="material-symbols-outlined text-base">add</span>
                </button>

                <span class="w-16 text-right text-sm font-semibold text-neon">
                  {{ (item.qty * item.product.priceCents) / 100 | number: '1.2-2' }} €
                </span>

                <button
                  type="button"
                  (click)="removeIngredient(i)"
                  class="w-8 h-8 rounded-full bg-white/10 hover:bg-red-500/40 transition-colors flex items-center justify-center ml-1"
                  data-testid="remove-ingredient">
                  <span class="material-symbols-outlined text-base">delete</span>
                </button>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="text-center py-4 text-white/40">
          <span class="material-symbols-outlined text-3xl mb-1">add_circle</span>
          <p class="text-sm">{{ 'pos.freestyle_drink.no_ingredients' | translate }}</p>
        </div>
      }
    </div>

    <div class="space-y-3">
      <div>
        <label class="text-xs text-white/50 block mb-1">{{ 'pos.freestyle_drink.name_label' | translate }}</label>
        <input
          type="text"
          [ngModel]="drinkName()"
          (ngModelChange)="drinkName.set($event)"
          [placeholder]="'pos.freestyle_drink.name_placeholder' | translate"
          class="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-neon"
          data-testid="drink-name-input" />
      </div>

      <div>
        <label class="text-xs text-white/50 block mb-1">{{
          'pos.freestyle_drink.extra_price_label' | translate
        }}</label>
        <div class="relative">
          <input
            type="number"
            [ngModel]="extraEur()"
            (ngModelChange)="extraEur.set(+$event)"
            min="0"
            step="0.5"
            class="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-neon pr-8"
            data-testid="extra-price-input" />
          <span class="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 text-sm">€</span>
        </div>
      </div>
    </div>
  </div>

  <div class="p-4 lg:p-6 border-t border-white/10">
    <div class="flex items-center justify-between mb-4">
      <span class="text-white/70 text-lg">{{ 'pos.freestyle_drink.total' | translate }}</span>
      <span
        class="text-3xl font-black text-neon"
        data-testid="total-price">
        {{ calculatedPrice() / 100 | number: '1.2-2' }} €
      </span>
    </div>

    <div class="flex gap-3">
      <button
        type="button"
        (click)="close()"
        class="flex-1 py-3 px-6 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
        <span class="material-symbols-outlined">arrow_back</span>
        {{ 'pos.freestyle_drink.cancel' | translate }}
      </button>

      <button
        type="button"
        (click)="submit()"
        [disabled]="!canSubmit()"
        class="flex-1 py-3 px-6 rounded-full bg-neon text-night font-bold hover:bg-neon/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 uppercase"
        data-testid="submit-button">
        @if (submitting()) {
          <span class="material-symbols-outlined animate-spin">progress_activity</span>
        } @else {
          <span class="material-symbols-outlined">check</span>
        }
        {{ 'pos.freestyle_drink.submit' | translate }}
      </button>
    </div>
  </div>
</div>
